import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MagnifyingGlass, Play, ArrowLeft, CaretRight, BookOpen, SignIn, ArrowUpRight } from "@phosphor-icons/react";
import axios from "axios";
import { KbMarkdown } from "../components/kbMarkdown";
import { KbWalkthrough } from "../components/KbWalkthrough";

/* Public Help Centre (no login) — fixlr.app/help and /help/<slug>. Same guides the embed widget shows on the marketing site. */
const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/help`;
const AUD_ICON = { admin: "Office", client: "Clients", vendor: "Suppliers", in_house: "Engineers" };

function useCatalogue() {
  const [cat, setCat] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { axios.get(`${API}/catalogue`).then((r) => setCat(r.data)).catch(() => setError(true)); }, []);
  return { cat, error };
}

function SearchBox({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(() => axios.get(`${API}/search`, { params: { q } }).then((r) => setResults(r.data.results)).catch(() => setResults([])), 250);
    return () => clearTimeout(timer.current);
  }, [q]);
  return (
    <div className="relative" data-testid="help-search">
      <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the guides — e.g. RAMS, payroll, approve a quote"
        className="w-full h-12 pl-11 pr-4 rounded-full border border-stone-300 bg-white text-stone-900 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-lime-500/60 focus:border-lime-500"
        data-testid="help-search-input" />
      {results && (
        <div className="absolute z-20 left-0 right-0 mt-2 bg-white border border-stone-200 rounded-2xl shadow-xl overflow-hidden max-h-[60vh] overflow-y-auto" data-testid="help-search-results">
          {results.length === 0 && <p className="px-4 py-3 text-sm text-stone-500">No guides match “{q}”.</p>}
          {results.map((r) => (
            <button key={r.slug} type="button" onClick={() => { setQ(""); setResults(null); onPick(r.slug); }}
              className="w-full text-left px-4 py-2.5 hover:bg-lime-50 border-b border-stone-100 last:border-0" data-testid={`help-search-hit-${r.slug}`}>
              <span className="block text-sm font-medium text-stone-900">{r.title}</span>
              <span className="block text-[11px] text-stone-500">{AUD_ICON[r.audience]} · {r.section_title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleRow({ a, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(a.slug)} className="w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-white hover:shadow-sm transition-colors group" data-testid={`help-article-${a.slug}`}>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 text-sm font-medium text-stone-900">
          {a.title}
          {a.has_walkthrough && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-lime-700 bg-lime-100 rounded-full px-1.5 py-px"><Play size={9} weight="fill" /> Watch</span>}
        </span>
        {a.summary && <span className="block text-xs text-stone-500 mt-0.5 line-clamp-2">{a.summary}</span>}
      </span>
      <CaretRight size={14} className="text-stone-300 group-hover:text-lime-600 mt-1 shrink-0" />
    </button>
  );
}

function Catalogue({ cat, audience, setAudience, onOpen }) {
  const aud = cat.audiences.find((a) => a.key === audience) || cat.audiences[0];
  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-8">
      <aside className="space-y-1" data-testid="help-audiences">
        {cat.audiences.map((a) => (
          <button key={a.key} type="button" onClick={() => setAudience(a.key)}
            className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${a.key === aud.key ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 hover:border-stone-400 text-stone-800"}`}
            data-testid={`help-audience-${a.key}`}>
            <span className="block text-sm font-semibold">{a.label}</span>
            <span className={`block text-[11px] mt-0.5 ${a.key === aud.key ? "text-stone-300" : "text-stone-500"}`}>{a.total} guides</span>
          </button>
        ))}
        {cat.featured?.length > 0 && (
          <div className="pt-4">
            <p className="px-1 text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-2">Narrated walkthroughs</p>
            {cat.featured.slice(0, 8).map((f) => (
              <button key={f.slug} type="button" onClick={() => onOpen(f.slug)} className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-stone-700 hover:bg-white" data-testid={`help-featured-${f.slug}`}>
                <Play size={10} weight="fill" className="text-lime-600 shrink-0" /> <span className="truncate">{f.title}</span>
              </button>
            ))}
          </div>
        )}
      </aside>
      <div>
        <p className="text-sm text-stone-600 mb-5" data-testid="help-audience-blurb">{aud.blurb}</p>
        <div className="space-y-7">
          {aud.sections.map((s) => (
            <section key={s.id} data-testid={`help-section-${s.id}`}>
              <h2 className="text-base md:text-lg font-semibold text-stone-900 font-[Chivo] mb-2 px-1">{s.title}</h2>
              <div className="rounded-2xl bg-stone-100/70 p-1.5">
                {s.articles.map((a) => <ArticleRow key={a.slug} a={a} onOpen={onOpen} />)}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function Article({ slug, onOpen, onBack }) {
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setA(null); setMissing(false);
    axios.get(`${API}/articles/${slug}`).then((r) => { setA(r.data); document.title = `${r.data.title} · Fixlr Help`; }).catch(() => setMissing(true));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);
  if (missing) return <div className="py-16 text-center text-stone-500" data-testid="help-article-missing">That guide isn't available. <button type="button" onClick={onBack} className="text-lime-700 underline">Back to all guides</button></div>;
  if (!a) return <div className="py-16 text-center text-stone-400 text-sm">Loading…</div>;
  return (
    <article className="max-w-3xl mx-auto" data-testid={`help-article-view-${slug}`}>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900 mb-5" data-testid="help-back"><ArrowLeft size={12} /> All guides</button>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-lime-700 mb-1">{AUD_ICON[a.audience]} · {a.section_title}</p>
      <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] text-stone-900 tracking-tight mb-2">{a.title}</h1>
      {a.summary && <p className="text-sm text-stone-600 italic mb-5">{a.summary}</p>}
      <KbWalkthrough walkthrough={a.walkthrough} slug={a.slug} />
      <div className="help-prose text-stone-800" data-testid="help-article-body">
        <KbMarkdown content={a.content} onOpenArticle={onOpen} />
      </div>
      {a.related?.length > 0 && (
        <div className="mt-8 border-t border-stone-200 pt-5" data-testid="help-related">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-2">Related guides</p>
          <div className="flex flex-wrap gap-2">
            {a.related.map((r) => (
              <button key={r.slug} type="button" onClick={() => onOpen(r.slug)} className="text-xs px-3 py-1.5 rounded-full border border-stone-300 bg-white hover:border-lime-600 hover:text-lime-700" data-testid={`help-related-${r.slug}`}>{r.title}</button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-10 rounded-2xl bg-stone-900 text-white p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-semibold font-[Chivo]">Ready to try it in your own workspace?</p>
          <p className="text-sm text-stone-300">Sign in, or start a free trial on fixlr.app.</p>
        </div>
        <a href={a.login_url} className="inline-flex items-center gap-1.5 rounded-full bg-lime-400 text-stone-900 text-sm font-semibold px-4 py-2 hover:bg-lime-300" data-testid="help-signin-cta"><SignIn size={16} /> Sign in</a>
      </div>
    </article>
  );
}

export default function PublicHelpCentrePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { cat, error } = useCatalogue();
  const [audience, setAudience] = useState("admin");
  useEffect(() => { if (!slug) document.title = "Fixlr Help Centre — guides for the office, clients, suppliers and engineers"; }, [slug]);
  const open = (s) => navigate(`/help/${s}`);
  const total = useMemo(() => cat?.total || 0, [cat]);
  return (
    <div className="min-h-screen bg-[#f6f5f1] text-stone-900" data-testid="public-help-centre">
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/help" className="flex items-center gap-3" data-testid="help-home-link">
            <img src="/fixlr-logo-dark.png" alt="Fixlr" className="h-8 w-auto" />
            <span className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-stone-700"><BookOpen size={16} className="text-lime-600" /> Help Centre</span>
          </Link>
          <nav className="flex items-center gap-2 text-sm">
            <a href="https://fixlr.me" className="hidden sm:inline-flex items-center gap-1 text-stone-600 hover:text-stone-900 px-3 py-1.5" data-testid="help-marketing-link">fixlr.me <ArrowUpRight size={14} /></a>
            <Link to="/login" className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white px-4 py-1.5 font-medium hover:bg-stone-700" data-testid="help-login-link"><SignIn size={14} /> Sign in</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {!slug && (
          <div className="mb-10 max-w-2xl">
            <h1 className="text-4xl sm:text-5xl font-bold font-[Chivo] tracking-tight text-stone-900 mb-3">How can we help?</h1>
            <p className="text-base text-stone-600 mb-6">{total ? `${total} guides` : "Guides"} with real screenshots and narrated walkthroughs — for the office, the client portal, suppliers and engineers.</p>
            <SearchBox onPick={open} />
          </div>
        )}
        {error && <p className="text-sm text-red-600" data-testid="help-error">The help centre is temporarily unavailable.</p>}
        {slug ? <Article slug={slug} onOpen={open} onBack={() => navigate("/help")} /> : cat ? <Catalogue cat={cat} audience={audience} setAudience={setAudience} onOpen={open} /> : !error && <p className="text-sm text-stone-400">Loading guides…</p>}
      </main>

      <footer className="border-t border-stone-200 py-8 text-center text-xs text-stone-500">
        © {new Date().getFullYear()} Fixlr · <a href="https://fixlr.me" className="hover:text-stone-900">fixlr.me</a> · <Link to="/status" className="hover:text-stone-900">System status</Link>
      </footer>
    </div>
  );
}
